import { uniq } from "lodash";
import { useCallback, useEffect, useMemo, useState } from "react";

import { useGetAggregatedEntityIds } from "@ds/hooks/use-get-aggregated-entity-ids";
import { useDeepRootSelector, useRootDispatch, useRootSelector } from "@ds/hooks";

import { selectPlayersByIds, selectTablePlayers } from "@ds/modules/devices/players/redux/selectors";
import { playersActions } from "@ds/modules/devices/players/redux/slice";
import { DetailsTableDataTypeEnum } from "@ds/modules/table-data/utils/model";

import { type DataTableRowReorderEvent } from "@ds/components/table/data-table";

import { experiencesActions } from "../../redux/slice";
import { selectExperiencesByIds } from "../../redux/selectors";

/** The experience's players in their order, and drag-to-reorder with its own save and cancel */
export const useExperiencePlayersReorder = (experience: Experience) => {
  const dispatch = useRootDispatch();

  const aggregatedExperienceIds = useGetAggregatedEntityIds(experience);
  const aggregatedExperiencePlayers = useDeepRootSelector(state => {
    const aggregatedExperiences = selectExperiencesByIds(state, aggregatedExperienceIds);
    const aggregatedPlayersIds = uniq(aggregatedExperiences.flatMap(({ device_ids }) => device_ids).filter(_ => _));
    return selectPlayersByIds(state, aggregatedPlayersIds, "experience_order");
  });

  const tablePlayers = useRootSelector(state => selectTablePlayers(state, DetailsTableDataTypeEnum.ExperiencePlayers));

  // Local state for optimistic reordering
  const [localPlayers, setLocalPlayers] = useState<Player[]>([]);

  // Initialize local state when aggregatedExperiencePlayers changes
  useEffect(() => {
    setLocalPlayers(aggregatedExperiencePlayers);
  }, [aggregatedExperiencePlayers]);

  // Sync local state with Redux table state
  useEffect(() => {
    if (localPlayers.length) {
      dispatch(
        playersActions.setTablePlayers(localPlayers, {
          tableType: DetailsTableDataTypeEnum.ExperiencePlayers,
        }),
      );
    }
  }, [dispatch, localPlayers]);

  // Detect if user has reordered players (dirty state)
  const isReordering = useMemo(() => {
    if (localPlayers.length !== aggregatedExperiencePlayers.length) return false;

    // Compare order by checking if player IDs match in sequence
    return !localPlayers.every((player, index) => player.id === aggregatedExperiencePlayers[index]?.id);
  }, [localPlayers, aggregatedExperiencePlayers]);

  const onReorderHandler = useCallback((currIndex: number, newIndex: number) => {
    if (currIndex !== newIndex) {
      // Update local state only
      setLocalPlayers(prevPlayers => {
        const reordered = [...prevPlayers];
        const [movedPlayer] = reordered.splice(currIndex, 1);
        reordered.splice(newIndex, 0, movedPlayer);
        return reordered;
      });
    }
  }, []);

  const onSaveReorderHandler = useCallback(() => {
    // Dispatch Redux action to persist the new order
    dispatch(
      experiencesActions.reorderExperiencePlayers({
        experienceId: experience.id,
        playerIds: localPlayers.map(p => p.id),
      }),
    );
  }, [dispatch, experience.id, localPlayers]);

  const onCancelReorderHandler = useCallback(() => {
    // Reset local state to original order
    setLocalPlayers(aggregatedExperiencePlayers);
  }, [aggregatedExperiencePlayers]);

  const onTableRowReorderHandler = useCallback(
    (e: DataTableRowReorderEvent<Player[]>) => {
      onReorderHandler(e.dragIndex, e.dropIndex);
    },
    [onReorderHandler],
  );

  return {
    aggregatedExperiencePlayers,
    tablePlayers,
    isReordering,
    onSaveReorderHandler,
    onCancelReorderHandler,
    onTableRowReorderHandler,
  };
};
